import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.tsx';
import { Timer, Zap, Crown, ArrowRight } from 'lucide-react';

interface LimitReachedModalProps {
    isOpen: boolean;
    onClose: () => void;
    onOpenPricing?: () => void;
    dailyRemaining?: number;
    boosters?: number;
    nextResetAt?: Date | string;
    hasMembership?: boolean;
}

const LimitReachedModal: React.FC<LimitReachedModalProps> = ({
    isOpen,
    onClose,
    onOpenPricing,
    dailyRemaining = 0,
    boosters = 0,
    nextResetAt,
    hasMembership = false
}) => {
    const { isDarkMode } = useTheme();
    const [countdown, setCountdown] = useState('');

    useEffect(() => {
        if (!isOpen || !nextResetAt) return;

        const updateCountdown = () => {
            const now = new Date();
            const reset = new Date(nextResetAt);
            const diff = reset.getTime() - now.getTime();

            if (diff <= 0) {
                setCountdown('¡Ya puedes enviar mensajes!');
                return;
            }

            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            setCountdown(`${hours}h ${minutes}m`);
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 60000);
        return () => clearInterval(interval);
    }, [isOpen, nextResetAt]);

    if (!isOpen) return null;

    const handleUpgrade = () => {
        onClose();
        if (onOpenPricing) {
            setTimeout(() => onOpenPricing(), 100);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(8px)',
            padding: '16px'
        }}>
            <div style={{
                background: isDarkMode
                    ? 'linear-gradient(135deg, #1e1e2e 0%, #2d1f3d 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: '24px',
                padding: '32px',
                maxWidth: '420px',
                width: '100%',
                textAlign: 'center',
                boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                border: `1px solid ${isDarkMode ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.2)'}`
            }}>
                {/* Icon */}
                <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                }}>
                    <Timer size={36} color="white" />
                </div>

                {/* Title */}
                <h2 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: isDarkMode ? '#e2e8f0' : '#1e293b',
                    marginBottom: '12px'
                }}
                >
                    Out of Messages!
                </h2>

                {/* Description */}
                <p style={{
                    fontSize: '14px',
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    marginBottom: '20px',
                    lineHeight: 1.6
                }}>
                    You've used all your daily messages.
                </p>

                {/* Countdown */}
                {nextResetAt && (
                    <div style={{
                        background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            fontSize: '12px',
                            color: isDarkMode ? '#94a3b8' : '#64748b',
                            marginBottom: '4px'
                        }}>
                            Next reset in:
                        </div>
                        <div style={{
                            fontSize: '28px',
                            fontWeight: '700',
                            background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            {countdown}
                        </div>
                    </div>
                )}

                {/* Boosters Info */}
                {boosters > 0 && (
                    <div style={{
                        background: 'rgba(16, 185, 129, 0.15)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: '12px',
                        padding: '12px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                    }}>
                        <Zap size={18} color="#10b981" />
                        <span style={{ color: '#10b981', fontWeight: '600', fontSize: '14px' }}>
                            You have {boosters.toLocaleString()} booster messages available!
                        </span>
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Use Boosters Button - Primary if available */}
                    {boosters > 0 && (
                        <button
                            onClick={onClose}
                            style={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '14px 24px',
                                borderRadius: '12px',
                                fontSize: '15px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                width: '100%',
                                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)'
                            }}
                        >
                            <Zap size={18} />
                            Continue with Boosters ({boosters.toLocaleString()} left)
                            <ArrowRight size={16} />
                        </button>
                    )}

                    {/* Upgrade Button */}
                    <button
                        onClick={handleUpgrade}
                        style={{
                            background: boosters > 0
                                ? 'transparent'
                                : 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                            color: boosters > 0 ? (isDarkMode ? '#a855f7' : '#9333ea') : 'white',
                            border: boosters > 0 ? `1px solid ${isDarkMode ? 'rgba(168, 85, 247, 0.5)' : 'rgba(168, 85, 247, 0.3)'}` : 'none',
                            padding: '14px 24px',
                            borderRadius: '12px',
                            fontSize: '15px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            width: '100%'
                        }}
                    >
                        {hasMembership ? (
                            <>
                                <Zap size={18} />
                                Buy More Boosters
                            </>
                        ) : (
                            <>
                                <Crown size={18} />
                                View Premium Plans
                            </>
                        )}
                        <ArrowRight size={16} />
                    </button>

                    {/* Close Button - Only show if no boosters */}
                    {boosters === 0 && (
                        <button
                            onClick={onClose}
                            style={{
                                backgroundColor: 'transparent',
                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                padding: '12px 24px',
                                borderRadius: '12px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                width: '100%'
                            }}
                        >
                            Wait for reset
                        </button>
                    )}
                </div>

                {/* Tip */}
                <p style={{
                    fontSize: '11px',
                    color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                    marginTop: '16px'
                }}>
                    💡 Daily messages reset every 24 hours
                </p>
            </div>
        </div>
    );
};

export default LimitReachedModal;
